import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getFamilles } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeFamillesPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [familles, setFamilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const commune = params.get('commune') || '';
    setSearchTerm(search);
    setSelectedCommune(commune);
  }, [location]);

  useEffect(() => {
    const loadFamilles = async () => {
      try {
        const response = await getFamilles();
        const famillesData = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
        setFamilles(famillesData);
      } catch (error) {
        console.error('Erreur lors du chargement des familles:', error);
        notificationService.error('Erreur', 'Impossible de charger les familles');
      } finally {
        setLoading(false);
      }
    };

    loadFamilles();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Familles${selectedCommune ? `_${selectedCommune.replace(/\s+/g, '_')}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les familles
  const filteredFamilles = familles.filter(famille => {
    // Filtre par commune
    if (selectedCommune && selectedCommune !== 'toutes' && famille.commune !== selectedCommune) {
      return false;
    }
    
    // Filtre par recherche
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (famille.nom_famille && famille.nom_famille.toLowerCase().includes(search)) ||
      (famille.chef_famille_nom && famille.chef_famille_nom.toLowerCase().includes(search)) ||
      (famille.numero_famille && famille.numero_famille.toLowerCase().includes(search)) ||
      (famille.telephone && famille.telephone.includes(search)) ||
      (famille.quartier && famille.quartier.toLowerCase().includes(search)) ||
      (famille.commune && famille.commune.toLowerCase().includes(search)) ||
      (famille.centre_sante && famille.centre_sante.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des familles...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>Liste des Familles - Impression{selectedCommune && selectedCommune !== 'toutes' ? ` - ${selectedCommune}` : ''}</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Familles{selectedCommune && selectedCommune !== 'toutes' ? ` - ${selectedCommune}` : ''}</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedCommune && selectedCommune !== 'toutes' && (
                  <p className="print-date" style={{ marginTop: '5px' }}>
                    <strong>Commune :</strong> {selectedCommune}
                  </p>
                )}
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Total :</strong> {filteredFamilles.length} famille(s)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Numéro</th>
                <th>Nom de famille</th>
                <th>Chef de famille</th>
                <th>Commune</th>
                <th>Quartier</th>
                <th>Centre de santé</th>
                <th>Téléphone</th>
                <th>Membres</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredFamilles.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm || selectedCommune ? 'Aucune famille trouvée' : 'Aucune famille enregistrée'}
                  </td>
                </tr>
              ) : (
                filteredFamilles.map((famille, index) => (
                  <tr key={famille.id}>
                    <td>{index + 1}</td>
                    <td>{famille.numero_famille || '-'}</td>
                    <td>{famille.nom_famille || '-'}</td>
                    <td>{famille.chef_famille_nom || '-'}</td>
                    <td>{famille.commune || '-'}</td>
                    <td>{famille.quartier || '-'}</td>
                    <td>{famille.centre_sante || '-'}</td>
                    <td>{famille.telephone || '-'}</td>
                    <td>{famille.nombre_membres || 1}</td>
                    <td>{(famille.statut === 'active' || !famille.statut) ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListeFamillesPrint;

